// src/pages/finance/Finance.jsx — money beyond invoices (owner / manager):
// Aging (who owes, how late) · Cheques · Owner & Loans · Salaries & Commissions · Fixed Assets · Close month.
// Everything here is booked by the server's accounting engine (utils/ledger.js).
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import useAuthStore from '../../store/authStore';
import { Btn, Card, DataTable, Field, MethodSelect, Modal, Spin, Stat, Tabs, errOf, fmtDate, inputCls, isoDay, money } from '../../components/kit';

const usePM = () => useQuery({ queryKey: ['pm-list'], queryFn: () => api.get('/api/payment-methods').then((r) => (Array.isArray(r.data) ? r.data : r.data?.data || [])) }).data || [];
const CUR = ['USD', 'LBP'];

export default function Finance() {
  const [tab, setTab] = useState('aging');
  const role = useAuthStore((s) => s.user?.role);
  const tabs = [
    { key: 'aging', label: '⏳ Aging (who owes)' },
    { key: 'cheques', label: '🧾 Cheques' },
    { key: 'moves', label: '🏦 Owner & Loans' },
    { key: 'payroll', label: '👥 Salaries & Commissions' },
    { key: 'assets', label: '🏭 Fixed Assets' },
    ...(role === 'admin' ? [{ key: 'close', label: '🔒 Close month' }] : []),
  ];
  return (
    <div className="p-5">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">💼 Finance</h1>
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === 'aging' && <AgingTab />}
      {tab === 'cheques' && <ChequesTab />}
      {tab === 'moves' && <MovesTab />}
      {tab === 'payroll' && <PayrollTab />}
      {tab === 'assets' && <AssetsTab />}
      {tab === 'close' && <CloseTab />}
    </div>
  );
}

/* ── Aging ─────────────────────────────────────────────────────────── */
function AgingTab() {
  const nav = useNavigate();
  const [side, setSide] = useState('customer');
  const [date, setDate] = useState(isoDay());
  const [open, setOpen] = useState(null);
  const { data, isLoading, error } = useQuery({ queryKey: ['aging', side, date], queryFn: () => api.get('/api/finance/aging', { params: { side, date } }).then((r) => r.data) });
  if (isLoading) return <Spin />;
  if (error) return <Card className="p-6 text-red-600">{errOf(error)}</Card>;
  const T = data.totals;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex rounded-lg border overflow-hidden">
          {[['customer', 'Customers owe us'], ['supplier', 'We owe suppliers']].map(([k, l]) => (
            <button key={k} onClick={() => setSide(k)} className={`px-4 py-2 text-sm font-semibold ${side === k ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`}>{l}</button>
          ))}
        </div>
        <Field label="As of"><input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat label="Not due yet" value={money(T.current)} tone="green" />
        <Stat label="1–30 days late" value={money(T.d1_30)} tone="amber" />
        <Stat label="31–60 days late" value={money(T.d31_60)} tone="amber" />
        <Stat label="61–90 days late" value={money(T.d61_90)} tone="red" />
        <Stat label="Over 90 days" value={money(T.d90_plus)} tone="red" />
        <Stat label="Total" value={money(T.total)} tone="blue" />
      </div>
      <DataTable
        rows={data.rows}
        onRow={(r) => setOpen(r)}
        rowClass={(r) => (r.over_limit ? 'bg-red-50' : '')}
        excel={{ filename: `aging_${side}_${date}`, title: `Aging — ${side === 'customer' ? 'customers' : 'suppliers'} (USD)`, subtitle: `As of ${date}` }}
        footerNote="due date = invoice date + the account's payment terms · red = over the credit limit · click a row for its invoices"
        columns={[
          { key: 'name', label: 'Account', width: 28 },
          { key: 'terms_days', label: 'Terms', render: (r) => (r.terms_days ? `${r.terms_days} days` : 'cash'), excelValue: (r) => r.terms_days },
          { key: 'credit_limit', label: 'Credit limit', money: true },
          { key: 'current', label: 'Not due', money: true, total: true },
          { key: 'd1_30', label: '1–30', money: true, total: true },
          { key: 'd31_60', label: '31–60', money: true, total: true },
          { key: 'd61_90', label: '61–90', money: true, total: true },
          { key: 'd90_plus', label: '90+', money: true, total: true },
          { key: 'total', label: 'Total', money: true, total: true, className: 'font-bold' },
        ]}
      />
      {open && (
        <Modal title={`${open.name} — open invoices`} onClose={() => setOpen(null)} wide>
          <DataTable rows={open.invoices} onRow={(i) => nav(`/documents/${i._id}`)} columns={[
            { key: 'number', label: 'Invoice' }, { key: 'date', label: 'Date', date: true }, { key: 'due_date', label: 'Due', date: true },
            { key: 'days_late', label: 'Days late', num: true }, { key: 'balance_usd', label: 'Balance', money: true, total: true },
          ]} />
          {open.account_id && <div className="mt-3 text-right"><Btn kind="light" onClick={() => nav(`/accounts/${open.account_id}/statement`)}>Open statement →</Btn></div>}
        </Modal>
      )}
    </div>
  );
}

/* ── Cheques ───────────────────────────────────────────────────────── */
function ChequesTab() {
  const qc = useQueryClient();
  const methods = usePM();
  const [status, setStatus] = useState('pending');
  const [clearing, setClearing] = useState(null);
  const [bankId, setBankId] = useState('');
  const [date, setDate] = useState(isoDay());
  const { data = [], isLoading } = useQuery({ queryKey: ['cheques', status], queryFn: () => api.get('/api/finance/cheques', { params: status ? { status } : {} }).then((r) => r.data) });
  const act = async (c, action, extra = {}) => {
    if (action === 'bounce' && !window.confirm(`Mark cheque ${c.cheque_number || ''} as BOUNCED? The invoice will be owed again.`)) return;
    try { await api.put(`/api/finance/cheques/${c._id}`, { action, date, ...extra }); qc.invalidateQueries(['cheques']); setClearing(null); } catch (e) { alert(errOf(e)); }
  };
  const due = data.filter((c) => c.cheque_status === 'pending' && new Date(c.cheque_date) <= new Date());
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {[['pending', 'Waiting'], ['cleared', 'Cashed'], ['bounced', 'Bounced'], ['', 'All']].map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${status === k ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600'}`}>{l}</button>
        ))}
      </div>
      {status === 'pending' && due.length > 0 && <Card className="p-3 bg-amber-50 border-amber-200 text-amber-800 text-sm">⏰ {due.length} cheque(s) can be cashed now (their date has come).</Card>}
      {isLoading ? <Spin /> : (
        <DataTable rows={data} excel={{ filename: `cheques_${status || 'all'}`, title: 'Cheques' }}
          footerNote="a cheque received stays in “Cheques received” until you mark it cashed (into a bank) or bounced"
          columns={[
            { key: 'direction', label: 'In/Out', render: (c) => (c.direction === 'in' ? '⬇ received' : '⬆ given') },
            { key: 'cheque_number', label: 'No.' }, { key: 'cheque_bank', label: 'Bank' },
            { key: 'cheque_date', label: 'Cheque date', date: true },
            { key: 'account', label: 'From / to' }, { key: 'document_number', label: 'Invoice' },
            { key: 'amount', label: 'Amount', render: (c) => `${Number(c.amount).toLocaleString()} ${c.currency}`, excelValue: (c) => c.amount },
            { key: 'cheque_status', label: 'Status', render: (c) => ({ pending: '⏳ waiting', cleared: `✅ cashed ${fmtDate(c.cleared_at)}`, bounced: `❌ bounced ${fmtDate(c.bounced_at)}` }[c.cheque_status]) },
            { key: 'actions', label: '', excel: false, render: (c) => (
              <div className="flex gap-1 justify-end">
                {c.cheque_status === 'pending' && <><Btn onClick={() => { setClearing(c); setBankId(''); }}>Cashed</Btn><Btn kind="danger" onClick={() => act(c, 'bounce')}>Bounced</Btn></>}
                {c.cheque_status !== 'pending' && <Btn kind="light" onClick={() => act(c, 'undo')}>Undo</Btn>}
              </div>) },
          ]} />
      )}
      {clearing && (
        <Modal title={`Cheque ${clearing.cheque_number || ''} cashed`} onClose={() => setClearing(null)}
          footer={<><Btn kind="light" onClick={() => setClearing(null)}>Cancel</Btn><Btn disabled={!bankId} onClick={() => act(clearing, 'clear', { method_id: bankId })}>Save</Btn></>}>
          <div className="space-y-3">
            <Field label="Went into (bank account)"><MethodSelect methods={methods} value={bankId} onChange={setBankId} filter={(m) => m.type !== 'cheque'} /></Field>
            <Field label="Date"><input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Owner & Loans (money moves) ───────────────────────────────────── */
const MOVE_KINDS = [
  ['capital_in', '💰 Owner puts money in (capital)'],
  ['drawing', '👛 Owner takes money out (drawings)'],
  ['loan_in', '🏦 Loan received'],
  ['loan_repay', '↩ Loan repayment'],
  ['loan_interest', '％ Loan interest / bank charges'],
  ['transfer', '🔁 Transfer between accounts (cash → bank…)'],
  ['other_income', '➕ Other income'],
];
function MovesTab() {
  const qc = useQueryClient();
  const methods = usePM();
  const role = useAuthStore((s) => s.user?.role);
  const [form, setForm] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ['moves'], queryFn: () => api.get('/api/finance/moves', { params: { kind: MOVE_KINDS.map((k) => k[0]).join(',') } }).then((r) => r.data) });
  const { data: loans = [] } = useQuery({ queryKey: ['loans'], queryFn: () => api.get('/api/finance/loans').then((r) => r.data) });
  const save = async () => {
    try { await api.post('/api/finance/moves', form); setForm(null); qc.invalidateQueries(['moves']); qc.invalidateQueries(['loans']); } catch (e) { alert(errOf(e)); }
  };
  const del = async (m) => { if (!window.confirm('Delete this entry?')) return; try { await api.delete(`/api/finance/moves/${m._id}`); qc.invalidateQueries(['moves']); qc.invalidateQueries(['loans']); } catch (e) { alert(errOf(e)); } };
  const label = Object.fromEntries(MOVE_KINDS);
  return (
    <div className="space-y-4">
      {role === 'admin' && <div className="flex flex-wrap gap-2">{MOVE_KINDS.map(([k, l]) => <Btn key={k} kind="light" onClick={() => setForm({ kind: k, amount: '', currency: 'USD', date: isoDay(), payment_method_id: '', to_method_id: '', party: '', note: '' })}>{l}</Btn>)}</div>}
      {loans.length > 0 && (
        <div className="grid md:grid-cols-3 gap-3">{loans.map((l) => <Stat key={l.lender} label={`Loan — ${l.lender}`} value={money(l.owed)} tone={l.owed > 0 ? 'red' : 'green'} hint={`received ${money(l.received)} · repaid ${money(l.repaid)} · interest ${money(l.interest)}`} />)}</div>
      )}
      {isLoading ? <Spin /> : (
        <DataTable rows={data.moves} excel={{ filename: 'owner_loans', title: 'Owner & loans' }} columns={[
          { key: 'date', label: 'Date', date: true },
          { key: 'kind', label: 'What', render: (m) => label[m.kind] || m.kind, excelValue: (m) => label[m.kind] || m.kind },
          { key: 'party', label: 'Who / lender' },
          { key: 'payment_method_name', label: 'Account', render: (m) => (m.kind === 'transfer' ? `${m.payment_method_name} → ${m.to_method_name}` : m.payment_method_name) },
          { key: 'amount', label: 'Amount', render: (m) => `${Number(m.amount).toLocaleString()} ${m.currency}`, excelValue: (m) => m.amount },
          { key: 'note', label: 'Note' },
          ...(role === 'admin' ? [{ key: 'x', label: '', excel: false, render: (m) => <button className="text-red-500 text-xs" onClick={() => del(m)}>delete</button> }] : []),
        ]} />
      )}
      {form && (
        <Modal title={label[form.kind]} onClose={() => setForm(null)} footer={<><Btn kind="light" onClick={() => setForm(null)}>Cancel</Btn><Btn onClick={save}>Save</Btn></>}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount"><input type="number" className={inputCls} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Currency"><select className={inputCls} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>{CUR.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label={form.kind === 'transfer' ? 'From account' : form.kind === 'capital_in' || form.kind === 'loan_in' || form.kind === 'other_income' ? 'Into account' : 'Paid from account'}>
              <MethodSelect methods={methods} value={form.payment_method_id} onChange={(v) => setForm({ ...form, payment_method_id: v })} filter={(m) => m.type !== 'cheque'} />
            </Field>
            {form.kind === 'transfer' ? (
              <Field label="To account"><MethodSelect methods={methods} value={form.to_method_id} onChange={(v) => setForm({ ...form, to_method_id: v })} filter={(m) => m.type !== 'cheque'} /></Field>
            ) : (
              <Field label={form.kind.startsWith('loan') ? 'Lender (bank / person)' : 'Who'}><input className={inputCls} value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} /></Field>
            )}
            <Field label="Date"><input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Note"><input className={inputCls} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Salaries & commissions ────────────────────────────────────────── */
function PayrollTab() {
  const qc = useQueryClient();
  const methods = usePM();
  const role = useAuthStore((s) => s.user?.role);
  const [month, setMonth] = useState(isoDay().slice(0, 7));
  const [emp, setEmp] = useState(null);
  const [pay, setPay] = useState(null);
  const first = `${month}-01`;
  const last = isoDay(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0));
  const { data, isLoading } = useQuery({ queryKey: ['payroll', month], queryFn: () => api.get('/api/finance/payroll', { params: { month } }).then((r) => r.data) });
  const { data: com } = useQuery({ queryKey: ['commissions', month], queryFn: () => api.get('/api/finance/commissions', { params: { date_from: first, date_to: last } }).then((r) => r.data) });
  const { data: users = [] } = useQuery({ queryKey: ['users-list'], queryFn: () => api.get('/api/users').then((r) => (Array.isArray(r.data) ? r.data : r.data?.data || [])), enabled: role === 'admin' });
  const refresh = () => { qc.invalidateQueries(['payroll']); qc.invalidateQueries(['commissions']); };
  const run = async () => { try { const r = await api.post('/api/finance/payroll/run', { month }); alert(`${r.data.created} salary(ies) recorded for ${month}.`); refresh(); } catch (e) { alert(errOf(e)); } };
  const record = async () => { try { const r = await api.post('/api/finance/commissions/record', { date_from: first, date_to: last }); alert(`${r.data.created} commission(s) recorded.`); refresh(); } catch (e) { alert(errOf(e)); } };
  const saveEmp = async () => {
    try { if (emp._id) await api.put(`/api/finance/employees/${emp._id}`, emp); else await api.post('/api/finance/employees', emp); setEmp(null); refresh(); } catch (e) { alert(errOf(e)); }
  };
  const savePay = async () => { try { await api.post('/api/finance/payroll/pay', pay); setPay(null); refresh(); } catch (e) { alert(errOf(e)); } };
  if (isLoading) return <Spin />;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <Field label="Month"><input type="month" className={inputCls} value={month} onChange={(e) => setMonth(e.target.value)} /></Field>
        {role === 'admin' && <>
          <Btn onClick={run}>Record salaries of {month}</Btn>
          <Btn kind="light" onClick={() => setEmp({ name: '', phone: '', role: '', salary: 0, currency: 'USD', commission_pct: 0, user_id: '' })}>+ Employee</Btn>
        </>}
      </div>
      <DataTable rows={data.employees} excel={{ filename: `salaries_${month}`, title: `Salaries — ${month}` }}
        footerNote="Owed = salaries + commissions recorded − paid (all time)"
        columns={[
          { key: 'name', label: 'Employee' }, { key: 'role', label: 'Job' },
          { key: 'salary', label: 'Monthly salary', render: (e) => `${Number(e.salary).toLocaleString()} ${e.currency}`, excelValue: (e) => e.salary },
          { key: 'commission_pct', label: 'Commission', render: (e) => (e.commission_pct ? `${e.commission_pct}%` : '—') },
          { key: 'salary_recorded_this_month', label: `${month}`, render: (e) => (e.salary_recorded_this_month ? '✅ recorded' : '—') },
          { key: 'owed', label: 'Owed to employee', money: true, total: true },
          ...(role === 'admin' ? [{ key: 'x', label: '', excel: false, render: (e) => (
            <div className="flex gap-1 justify-end">
              <Btn kind="light" onClick={() => setPay({ employee_id: e._id, name: e.name, amount: e.owed > 0 ? e.owed : e.salary, currency: 'USD', payment_method_id: '', date: isoDay(), advance: false })}>Pay</Btn>
              <Btn kind="light" onClick={() => setEmp({ ...e, user_id: e.user_id || '' })}>Edit</Btn>
            </div>) }] : []),
        ]} />
      {com && com.rows.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between"><h3 className="font-bold text-gray-700">Commissions on sales — {month}</h3>{role === 'admin' && <Btn kind="light" onClick={record}>Record commissions</Btn>}</div>
          <DataTable rows={com.rows} columns={[
            { key: 'name', label: 'Employee' }, { key: 'commission_pct', label: '%', num: true },
            { key: 'sales_usd', label: 'Sales (no VAT)', money: true, total: true }, { key: 'commission_usd', label: 'Commission', money: true, total: true },
            { key: 'recorded', label: '', render: (r) => (r.recorded ? '✅ recorded' : '') },
          ]} />
        </div>
      )}
      {emp && (
        <Modal title={emp._id ? `Edit ${emp.name}` : 'New employee'} onClose={() => setEmp(null)} footer={<><Btn kind="light" onClick={() => setEmp(null)}>Cancel</Btn><Btn onClick={saveEmp}>Save</Btn></>}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><input className={inputCls} value={emp.name} onChange={(e) => setEmp({ ...emp, name: e.target.value })} /></Field>
            <Field label="Phone"><input className={inputCls} value={emp.phone} onChange={(e) => setEmp({ ...emp, phone: e.target.value })} /></Field>
            <Field label="Job"><input className={inputCls} value={emp.role} onChange={(e) => setEmp({ ...emp, role: e.target.value })} /></Field>
            <Field label="Monthly salary"><div className="flex gap-1"><input type="number" className={inputCls} value={emp.salary} onChange={(e) => setEmp({ ...emp, salary: e.target.value })} /><select className={inputCls + ' w-24'} value={emp.currency} onChange={(e) => setEmp({ ...emp, currency: e.target.value })}>{CUR.map((c) => <option key={c}>{c}</option>)}</select></div></Field>
            <Field label="Commission % on sales" hint="on the sales made by the linked login"><input type="number" className={inputCls} value={emp.commission_pct} onChange={(e) => setEmp({ ...emp, commission_pct: e.target.value })} /></Field>
            <Field label="Linked login (for commissions)"><select className={inputCls} value={emp.user_id || ''} onChange={(e) => setEmp({ ...emp, user_id: e.target.value })}><option value="">— none —</option>{users.map((u) => <option key={u._id} value={u._id}>{u.name} ({u.role})</option>)}</select></Field>
            {emp._id && <Field label="Active"><select className={inputCls} value={emp.active === false ? 'no' : 'yes'} onChange={(e) => setEmp({ ...emp, active: e.target.value === 'yes' })}><option value="yes">Yes</option><option value="no">No (left)</option></select></Field>}
          </div>
        </Modal>
      )}
      {pay && (
        <Modal title={`Pay ${pay.name}`} onClose={() => setPay(null)} footer={<><Btn kind="light" onClick={() => setPay(null)}>Cancel</Btn><Btn onClick={savePay}>Save</Btn></>}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount"><input type="number" className={inputCls} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></Field>
            <Field label="Currency"><select className={inputCls} value={pay.currency} onChange={(e) => setPay({ ...pay, currency: e.target.value })}>{CUR.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Paid from"><MethodSelect methods={methods} value={pay.payment_method_id} onChange={(v) => setPay({ ...pay, payment_method_id: v })} filter={(m) => m.type !== 'cheque'} /></Field>
            <Field label="Date"><input type="date" className={inputCls} value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} /></Field>
            <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={pay.advance} onChange={(e) => setPay({ ...pay, advance: e.target.checked })} /> This is an advance (سلفة)</label>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Fixed assets ──────────────────────────────────────────────────── */
function AssetsTab() {
  const qc = useQueryClient();
  const methods = usePM();
  const role = useAuthStore((s) => s.user?.role);
  const [form, setForm] = useState(null);
  const [dispose, setDispose] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ['assets'], queryFn: () => api.get('/api/finance/assets').then((r) => r.data) });
  const save = async () => { try { if (form._id) await api.put(`/api/finance/assets/${form._id}`, form); else await api.post('/api/finance/assets', form); setForm(null); qc.invalidateQueries(['assets']); } catch (e) { alert(errOf(e)); } };
  const doDispose = async () => { try { await api.post(`/api/finance/assets/${dispose._id}/dispose`, dispose); setDispose(null); qc.invalidateQueries(['assets']); } catch (e) { alert(errOf(e)); } };
  const del = async (a) => { if (!window.confirm(`Delete ${a.name}? (only for a mistake — to sell / scrap it use "Sold / scrapped")`)) return; try { await api.delete(`/api/finance/assets/${a._id}`); qc.invalidateQueries(['assets']); } catch (e) { alert(errOf(e)); } };
  if (isLoading) return <Spin />;
  const live = data.filter((a) => !a.disposed_at);
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-3">
        <Stat label="Cost of assets" value={money(live.reduce((s, a) => s + a.cost_usd, 0))} />
        <Stat label="Depreciated so far" value={money(live.reduce((s, a) => s + a.depreciated_usd, 0))} tone="amber" />
        <Stat label="Book value today" value={money(live.reduce((s, a) => s + a.book_value_usd, 0))} tone="blue" hint={`${money(live.reduce((s, a) => s + (a.book_value_usd > 0 ? a.monthly_usd : 0), 0))} depreciation per month`} />
      </div>
      {role === 'admin' && <Btn onClick={() => setForm({ name: '', category: '', cost: '', currency: 'USD', date: isoDay(), life_months: 60, salvage: 0, payment_method_id: '', paid_by: 'method', note: '' })}>+ Asset (equipment, car, furniture…)</Btn>}
      <DataTable rows={data} excel={{ filename: 'fixed_assets', title: 'Fixed assets' }}
        footerNote="depreciation is booked automatically at the end of every month (straight line)"
        rowClass={(a) => (a.disposed_at ? 'text-gray-400' : '')}
        columns={[
          { key: 'name', label: 'Asset' }, { key: 'category', label: 'Type' }, { key: 'date', label: 'Bought', date: true },
          { key: 'cost_usd', label: 'Cost', money: true, total: true },
          { key: 'life_months', label: 'Life', render: (a) => `${a.life_months} months` , excelValue: (a) => a.life_months },
          { key: 'monthly_usd', label: 'Per month', money: true },
          { key: 'depreciated_usd', label: 'Depreciated', money: true, total: true },
          { key: 'book_value_usd', label: 'Book value', money: true, total: true, className: 'font-bold' },
          { key: 'disposed_at', label: 'Status', render: (a) => (a.disposed_at ? `sold/scrapped ${fmtDate(a.disposed_at)}` : 'in use') },
          ...(role === 'admin' ? [{ key: 'x', label: '', excel: false, render: (a) => !a.disposed_at && (
            <div className="flex gap-1 justify-end">
              <Btn kind="light" onClick={() => setForm({ ...a, date: isoDay(new Date(a.date)) })}>Edit</Btn>
              <Btn kind="light" onClick={() => setDispose({ _id: a._id, name: a.name, amount: 0, currency: 'USD', payment_method_id: '', date: isoDay() })}>Sold / scrapped</Btn>
              <button className="text-red-500 text-xs px-2" onClick={() => del(a)}>delete</button>
            </div>) }] : []),
        ]} />
      {form && (
        <Modal title={form._id ? `Edit ${form.name}` : 'New fixed asset'} onClose={() => setForm(null)} footer={<><Btn kind="light" onClick={() => setForm(null)}>Cancel</Btn><Btn onClick={save}>Save</Btn></>}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Type"><input className={inputCls} placeholder="equipment, car, furniture…" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
            <Field label="Cost"><div className="flex gap-1"><input type="number" className={inputCls} value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /><select className={inputCls + ' w-24'} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} disabled={!!form._id}>{CUR.map((c) => <option key={c}>{c}</option>)}</select></div></Field>
            <Field label="Bought on"><input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Useful life (months)" hint="60 = 5 years"><input type="number" className={inputCls} value={form.life_months} onChange={(e) => setForm({ ...form, life_months: e.target.value })} /></Field>
            <Field label="Value at the end (salvage)"><input type="number" className={inputCls} value={form.salvage} onChange={(e) => setForm({ ...form, salvage: e.target.value })} /></Field>
            {!form._id && <>
              <Field label="Paid by"><select className={inputCls} value={form.paid_by} onChange={(e) => setForm({ ...form, paid_by: e.target.value })}><option value="method">The shop (cash / bank)</option><option value="owner">The owner, from his pocket (capital)</option><option value="payable">Not paid yet (owed)</option></select></Field>
              {form.paid_by === 'method' && <Field label="From account"><MethodSelect methods={methods} value={form.payment_method_id} onChange={(v) => setForm({ ...form, payment_method_id: v })} /></Field>}
            </>}
          </div>
        </Modal>
      )}
      {dispose && (
        <Modal title={`${dispose.name} — sold or scrapped`} onClose={() => setDispose(null)} footer={<><Btn kind="light" onClick={() => setDispose(null)}>Cancel</Btn><Btn onClick={doDispose}>Save</Btn></>}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sold for (0 if scrapped)"><input type="number" className={inputCls} value={dispose.amount} onChange={(e) => setDispose({ ...dispose, amount: e.target.value })} /></Field>
            <Field label="Currency"><select className={inputCls} value={dispose.currency} onChange={(e) => setDispose({ ...dispose, currency: e.target.value })}>{CUR.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Money went into"><MethodSelect methods={methods} value={dispose.payment_method_id} onChange={(v) => setDispose({ ...dispose, payment_method_id: v })} allowEmpty /></Field>
            <Field label="Date"><input type="date" className={inputCls} value={dispose.date} onChange={(e) => setDispose({ ...dispose, date: e.target.value })} /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Close month ───────────────────────────────────────────────────── */
function CloseTab() {
  const qc = useQueryClient();
  const lastMonthEnd = isoDay(new Date(new Date().getFullYear(), new Date().getMonth(), 0));
  const [date, setDate] = useState(lastMonthEnd);
  const [busy, setBusy] = useState(false);
  const { data = [], isLoading } = useQuery({ queryKey: ['closings'], queryFn: () => api.get('/api/finance/closings').then((r) => r.data) });
  const close = async () => {
    if (!window.confirm(`Close the books up to ${date}?\n\nNothing dated on or before this day can be added, changed or deleted afterwards (the owner can move the lock date back in Accounting if really needed).`)) return;
    setBusy(true);
    try { const r = await api.post('/api/finance/close', { date }); alert(`Closed up to ${date}. Net profit of the month: ${money(r.data.net_profit)}`); qc.invalidateQueries(['closings']); qc.invalidateQueries(['settings']); }
    catch (e) { alert(errOf(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-3 max-w-xl">
        <p className="text-sm text-gray-600">Closing a month checks that the balance sheet balances, keeps a snapshot of the month (profit, assets, liabilities, equity) and <b>locks</b> everything up to that day — like an accountant closing the books.</p>
        <p className="text-xs text-gray-400">Before closing: record the month's salaries, check the cheques and count the stock.</p>
        <div className="flex gap-2 items-end">
          <Field label="Close up to"><input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Btn onClick={close} disabled={busy}>🔒 Close</Btn>
        </div>
      </Card>
      {isLoading ? <Spin /> : (
        <DataTable rows={data} excel={{ filename: 'closings', title: 'Closed months' }} columns={[
          { key: 'month', label: 'Month' }, { key: 'date', label: 'Locked up to', date: true },
          { key: 'net_sales', label: 'Net sales', money: true }, { key: 'net_profit', label: 'Net profit', money: true },
          { key: 'total_assets', label: 'Assets', money: true }, { key: 'total_liabilities', label: 'Liabilities', money: true }, { key: 'total_equity', label: 'Equity', money: true },
          { key: 'closed_by', label: 'By' }, { key: 'closed_at', label: 'On', date: true },
        ]} />
      )}
    </div>
  );
}
